import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { action } from "mobx";
import { observer } from "mobx-react";
import { routes } from "@/router/Routes";
import { Button, Box, TextField, Typography } from "@mui/material";
import { interactionStore, authStore, snackStore } from "@/stores/Stores";
import { Page } from "@/components/Page";

export const ResetPassword = observer(() => {
  const { t } = useTranslation();

  const emailInputRef = useRef<HTMLInputElement>(null);
  const [emailTouched, setEmailTouched] = useState(false);

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Password managers can fill the input without firing change events, so read the input on submit.
    action(() => {
      authStore.unknownUser.email = emailInputRef.current?.value ?? "";
    })();
    setEmailTouched(true);

    if (!authStore.unknownUser.canResetPassword || interactionStore.loading) {
      return;
    }

    await authStore.requestPasswordReset();
    if (authStore.passwordResetStatus === "success") {
      snackStore.showSuccess(`${t("Password reset successful")}. ${t("Please check your email, including your spam folder")}.`);
    } else if (authStore.passwordResetStatus === "error") {
      snackStore.showError(`${t("Reset password request failed")}. ${t("Please try again")}.`)
    }
  };

  const showEmailError = emailTouched && !authStore.unknownUser.isEmailValid;

  return (
    <Page title="Reset Password" showLanguages>
      <form onSubmit={onSubmitHandler} noValidate>
        <TextField
          inputRef={emailInputRef}
          onChange={action((event: ChangeEvent<HTMLInputElement>) => {
            authStore.unknownUser.email = event.target.value;
          })}
          onBlur={() => setEmailTouched(true)}
          fullWidth
          name="email"
          type="email"
          label={t("Email address")}
          variant="outlined"
          size="medium"
          error={showEmailError}
          helperText={showEmailError ? t("Invalid email address") : ""}
          autoComplete="username"
        />

        <Button
          disabled={interactionStore.loading}
          fullWidth
          type="submit"
          variant="contained"
          color="primary"
          sx={{ marginTop: "1rem" }}
        >
          {t("Reset Password")}
        </Button>
      </form>

      <Box mt="1rem">
        <Typography variant="body2" style={{ marginTop: "2rem" }}>
          {t("Need an account?")} <Link to={routes.pages.signup}>{t("Register")}</Link>
        </Typography>
      </Box>
    </Page>
  );
});
